import React, { useRef, useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import {
  aICreatorUploadAttachmentAction,
  aICreatorCheckAttachmentStatusAction,
  aICreatorGetAttachmentTitlesAction,
} from 'src/reducers/ai-creator/action'
import styles from './styles'
import { AttachmentUploader, ContentSelector } from './components'

const PdfView = ({ classes, onChange }) => {
  const dispatch = useDispatch()
  const attachments = useSelector(state => state.aICreator.attachments)
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [selectedTitle, setSelectedTitle] = useState(null)
  const [flattenedHeaders, setFlattenedHeaders] = useState([])
  const fileInputRef = useRef(null)

  const handleUploadFile = file => {
    const allowedExtensions = ['pdf', 'docx', 'doc']
    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      alert('Invalid file type. Please select a PDF, DOCX, or DOC file.')
      return
    }
    setAttachmentLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    dispatch(
      aICreatorUploadAttachmentAction(formData, file.name, () => {
        setAttachmentLoading(false)
      }),
    )
  }

  const checkAttachmentStatus = async () => {
    if (!isChecking && attachments.length > 0) {
      setIsChecking(true)
      for (const attachment of attachments) {
        if (!attachment.succeeded) {
          dispatch(
            aICreatorCheckAttachmentStatusAction(
              attachment.attachment_id,
              () => {},
            ),
          )
        }
      }
      setIsChecking(false)
    }
  }

  const getAttachmentTitles = attachmentIds => {
    dispatch(
      aICreatorGetAttachmentTitlesAction(attachmentIds, response => {
        if (response) {
          let allHeaders = []
          response.forEach(doc => {
            if (doc?.attachment?.headers) {
              const flattened = flattenHeaders(
                doc.attachment.headers,
                0,
                '',
                doc.attachment,
              )
              allHeaders.push({
                fileName: doc.attachment.name,
                headers: flattened,
              })
            }
          })
          setFlattenedHeaders(allHeaders)
        }
      }),
    )
  }

  const handleTitleSelect = title => {
    setSelectedTitle(title)
    if (onChange) {
      onChange({
        selectedContent: title,
        attachments: title,
      })
    }
  }

  useEffect(() => {
    const interval = setInterval(checkAttachmentStatus, 3000)
    return () => clearInterval(interval)
  }, [attachments])

  useEffect(() => {
    if (attachments.length > 0) {
      const processedAttachments = attachments.filter(att => att.succeeded)
      if (processedAttachments.length > 0) {
        getAttachmentTitles(processedAttachments.map(att => att.attachment_id))
      }
    }
  }, [attachments])

  return (
    <div className={classes.container}>
      <div className={classes.uploadSection}>
        <AttachmentUploader
          attachments={attachments}
          fileInputRef={fileInputRef}
          onUploadFile={handleUploadFile}
          loading={attachmentLoading}
        />
      </div>

      {flattenedHeaders.length > 0 && (
        <ContentSelector
          flattenedHeaders={flattenedHeaders}
          selectedTitle={selectedTitle}
          onTitleSelect={handleTitleSelect}
        />
      )}
    </div>
  )
}

const flattenHeaders = (
  headers,
  level = 0,
  parentTitle = '',
  attachmentData = null,
) => {
  let flattened = []
  headers.forEach(header => {
    const indent = '\u00A0'.repeat(level * 4)
    const title = header.element_id
    flattened.push({
      ...header,
      level,
      displayTitle: indent + title,
      fullPath: parentTitle ? `${parentTitle} > ${title}` : title,
      attachment_id: attachmentData?.id,
      url: attachmentData?.url,
      fileName: attachmentData?.name,
    })
    if (header.children && header.children.length > 0) {
      flattened = flattened.concat(
        flattenHeaders(header.children, level + 1, title, attachmentData),
      )
    }
  })
  return flattened
}

export default withStyles(styles)(PdfView)
